import { climateGeometry } from './climate-geometry';
import { world, type BodyComposition } from './composition-definition';

export const enum BodySurfaceClass {
  Rocky = 0,
  Terrestrial = 1,
  Desert = 2,
  Jovian = 3,
  IceGiant = 4,
}

export interface BodyAppearance {
  readonly surfaceClass: BodySurfaceClass;
  readonly atmosphereStrength: number;
  readonly liquidLevel: number;
  readonly greenLifeStrength: number;
  readonly terrainSeed: number;
  readonly lowColor: readonly [number, number, number];
  readonly highColor: readonly [number, number, number];
  readonly liquidColor: readonly [number, number, number];
  readonly atmosphereColor: readonly [number, number, number];
}

export interface PlanetEcologyProfile {
  readonly supportsAlgae: boolean;
  readonly productivity: number;
  /** Absolute biomass reserve, independent of the normalized rendering field. */
  readonly carryingCapacity: number;
  readonly nutrientEfficiency: number;
  readonly seasonalAmplitude: number;
  readonly consumerPressure: number;
}

export interface BodyDefinition {
  readonly index: number;
  readonly parentIndex: number;
  readonly orbitRadius: number;
  readonly phase: number;
  readonly mass: number;
  readonly orbitalMass: number;
  readonly bodyRadius: number;
  /** Legacy starting metadata; runtime drag/avoidance use atmosphere-profile.ts. */
  readonly atmosphereHeight: number;
  readonly atmosphereDamping: number;
  readonly angularSpeed: number;
  readonly composition: BodyComposition;
  readonly appearance: BodyAppearance;
  readonly spinRate: number;
  readonly spinOffset: number;
  readonly seasonPhase: number;
  readonly ecology: PlanetEcologyProfile;
}

/** Authoritative body records. Array order is the stable entity-slot contract. */
export const BODY_DEFINITIONS: readonly BodyDefinition[] = [
  {
    index: 1,
    orbitRadius: 0.42,
    phase: 2.85,
    mass: 4000,
    orbitalMass: 0.012,
    bodyRadius: 0.022,
    atmosphereHeight: 0.004,
    atmosphereDamping: 0.2,
    parentIndex: 0,
    angularSpeed: 0,
    composition: world('Cinderwake', 650, climateGeometry(3200000, 3, 1500, 0.13), { iron: 0.8, basalt: 0.2, silica: 0.4, carbonDioxide: 0.01, hydrogen: 0.002, helium: 0.001 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 0.08, 0, 0, 0.13, [0.12, 0.12, 0.13], [0.52, 0.48, 0.4], [0.025, 0.13, 0.2], [0.52, 0.5, 0.46]),
    spinRate: 0.21,
    spinOffset: 0.8,
    seasonPhase: 0 * 1.7,
    ecology: {
      supportsAlgae: true,
      productivity: 0.34,
      carryingCapacity: 97.28,
      nutrientEfficiency: 0.52,
      seasonalAmplitude: 0.18,
      consumerPressure: 1.42,
    },
  },
  {
    index: 2,
    orbitRadius: 0.72,
    phase: -1.72,
    mass: 2100,
    orbitalMass: 0.035,
    bodyRadius: 0.038,
    atmosphereHeight: 0.018,
    atmosphereDamping: 2.4,
    parentIndex: 0,
    angularSpeed: 0,
    composition: world('Glass Hollow', 305, climateGeometry(5500000, 7.5, 70, 0.29), { nitrogen: 0.32, carbonDioxide: 0.085, oxygen: 0.003, water: 0.42, silica: 0.95, silicate: 0.16, basalt: 0.03 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 1.15, 0.42, 0.35, 0.29, [0.3, 0.12, 0.025], [0.88, 0.6, 0.18], [0.06, 0.13, 0.17], [1, 0.68, 0.25]),
    spinRate: -0.08,
    spinOffset: 1.7,
    seasonPhase: 1 * 1.7,
    ecology: {
      supportsAlgae: true,
      productivity: 0.58,
      carryingCapacity: 158.72,
      nutrientEfficiency: 0.72,
      seasonalAmplitude: 0.08,
      consumerPressure: 1.2,
    },
  },
  {
    index: 3,
    orbitRadius: 1.02,
    phase: 2.22,
    mass: 2880,
    orbitalMass: 0.045,
    bodyRadius: 0.044,
    atmosphereHeight: 0.018,
    atmosphereDamping: 1.8,
    parentIndex: 0,
    angularSpeed: 0,
    composition: world('Verdant Hearth', 288, climateGeometry(6371000, 9.81, 60, 0.47), { nitrogen: 0.765, oxygen: 0.21, carbonDioxide: 0.025, water: 0.72, iron: 0.09, ironOxide: 0.11, basalt: 0.18, silicate: 0.62 }),
    appearance: bodyAppearance(BodySurfaceClass.Terrestrial, 0.86, 0.62, 1, 0.47, [0.16, 0.09, 0.035], [0.53, 0.36, 0.14], [0.008, 0.15, 0.31], [0.1, 0.56, 1]),
    spinRate: 0.12,
    spinOffset: 0.3,
    seasonPhase: 2 * 1.7,
    ecology: {
      supportsAlgae: true,
      productivity: 1,
      carryingCapacity: 256,
      nutrientEfficiency: 1,
      seasonalAmplitude: 0.16,
      consumerPressure: 0.9,
    },
  },
  {
    index: 4,
    orbitRadius: 1.36,
    phase: -0.62,
    mass: 1500,
    orbitalMass: 0.018,
    bodyRadius: 0.032,
    atmosphereHeight: 0.009,
    atmosphereDamping: 0.7,
    parentIndex: 0,
    angularSpeed: 0,
    composition: world('Rust Choir', 295, climateGeometry(3500000, 3.7, 110, 0.61), { carbonDioxide: 0.18, nitrogen: 0.05, oxygen: 0.002, water: 0.22, ironOxide: 0.78, iron: 0.05, basalt: 0.25, silicate: 0.12 }),
    appearance: bodyAppearance(BodySurfaceClass.Desert, 0.32, 0.22, 0.35, 0.61, [0.27, 0.035, 0.012], [0.75, 0.2, 0.045], [0.06, 0.11, 0.15], [0.95, 0.22, 0.06]),
    spinRate: 0.1,
    spinOffset: 1.4,
    seasonPhase: 3 * 1.7,
    ecology: {
      supportsAlgae: true,
      productivity: 0.42,
      carryingCapacity: 122.88,
      nutrientEfficiency: 0.58,
      seasonalAmplitude: 0.42,
      consumerPressure: 1.34,
    },
  },
  {
    index: 5,
    orbitRadius: 2.65,
    phase: 0.55,
    mass: 3760,
    orbitalMass: 0.24,
    bodyRadius: 0.058,
    atmosphereHeight: 0.024,
    atmosphereDamping: 2.6,
    parentIndex: 0,
    angularSpeed: 0,
    composition: world('Amber Deep', 180, climateGeometry(65000000, 22, 1000, 0.18), { hydrogen: 6, helium: 1.1, ammonia: 1.3, methane: 0.22, water: 0.09 },
    { layer: 'envelope', internalHeatFluxWm2: 38, thermalCapacity: 30 }),
    appearance: bodyAppearance(BodySurfaceClass.Jovian, 1.08, 0, 0, 0.18, [0.38, 0.11, 0.035], [0.9, 0.72, 0.46], [0.2, 0.12, 0.06], [0.96, 0.67, 0.31]),
    spinRate: 0.3,
    spinOffset: -0.6,
    seasonPhase: 4 * 1.7,
    ecology: {
      supportsAlgae: false,
      productivity: 0,
      carryingCapacity: 1,
      nutrientEfficiency: 0.82,
      seasonalAmplitude: 0.08,
      consumerPressure: 1.08,
    },
  },
  {
    index: 6,
    orbitRadius: 3.25,
    phase: -2.48,
    mass: 3300,
    orbitalMass: 0.14,
    bodyRadius: 0.053,
    atmosphereHeight: 0.022,
    atmosphereDamping: 2.2,
    parentIndex: 0,
    angularSpeed: 0,
    composition: world('Pale Lantern', 160, climateGeometry(4800000, 5.5, 300, 0.73), { nitrogen: 2.2, ammonia: 1.7, carbonDioxide: 0.08, water: 0.15, carbonate: 0.8, silica: 0.7 },
    { thermalCapacity: 20 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 0.94, 0, 0, 0.73, [0.42, 0.28, 0.12], [0.9, 0.79, 0.56], [0.22, 0.16, 0.08], [0.92, 0.78, 0.52]),
    spinRate: 0.26,
    spinOffset: 2.2,
    seasonPhase: 5 * 1.7,
    ecology: {
      supportsAlgae: false,
      productivity: 0,
      carryingCapacity: 1,
      nutrientEfficiency: 0.78,
      seasonalAmplitude: 0.12,
      consumerPressure: 1.12,
    },
  },
  {
    index: 7,
    orbitRadius: 3.85,
    phase: 1.52,
    mass: 2400,
    orbitalMass: 0.07,
    bodyRadius: 0.043,
    atmosphereHeight: 0.018,
    atmosphereDamping: 1.4,
    parentIndex: 0,
    angularSpeed: 0,
    composition: world('Blue Hush', 100, climateGeometry(4200000, 5, 220, 0.36), { nitrogen: 0.9, methane: 1.1, ammonia: 0.06, water: 0.08, basalt: 0.6, silica: 0.2 },
    { thermalCapacity: 20 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 0.88, 0.6, 0, 0.36, [0.035, 0.28, 0.34], [0.28, 0.76, 0.79], [0.04, 0.22, 0.3], [0.2, 0.82, 0.88]),
    spinRate: -0.16,
    spinOffset: -1.1,
    seasonPhase: 6 * 1.7,
    ecology: {
      supportsAlgae: false,
      productivity: 0,
      carryingCapacity: 1,
      nutrientEfficiency: 0.7,
      seasonalAmplitude: 0.26,
      consumerPressure: 1.18,
    },
  },
  {
    index: 8,
    orbitRadius: 4.45,
    phase: -0.08,
    mass: 2600,
    orbitalMass: 0.08,
    bodyRadius: 0.044,
    atmosphereHeight: 0.019,
    atmosphereDamping: 1.6,
    parentIndex: 0,
    angularSpeed: 0,
    composition: world('Last Ember', 1800, climateGeometry(4000000, 6, 300, 0.84), { nitrogen: 0.04, carbonDioxide: 0.04, hydrogen: 0.004, basalt: 0.75, silicate: 0.2, iron: 0.7 },
    { internalHeatFluxWm2: 370_000, thermalCapacity: 25 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 0.98, 0.7, 0, 0.84, [0.018, 0.075, 0.36], [0.15, 0.42, 0.9], [0.02, 0.09, 0.3], [0.12, 0.38, 1]),
    spinRate: 0.2,
    spinOffset: 0.1,
    seasonPhase: 7 * 1.7,
    ecology: {
      supportsAlgae: false,
      productivity: 0,
      carryingCapacity: 1,
      nutrientEfficiency: 0.64,
      seasonalAmplitude: 0.48,
      consumerPressure: 1.28,
    },
  },
  {
    index: 9,
    orbitRadius: 0.11,
    bodyRadius: 0.004,
    phase: -0.78,
    angularSpeed: -1.08,
    mass: 180,
    orbitalMass: 0.0007,
    parentIndex: 1,
    atmosphereHeight: 0,
    atmosphereDamping: 0,
    composition: world('Coal Shard', 500, climateGeometry(450000, 0.2, 1500, 0.07), { basalt: 0.6, carbon: 0.2, iron: 0.35 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 0, 0, 0, 0.07, [0.11, 0.115, 0.12], [0.48, 0.49, 0.47], [0.02, 0.035, 0.05], [0.45, 0.48, 0.5]),
    spinRate: 0.14,
    spinOffset: 0.4,
    seasonPhase: 8 * 1.7,
    ecology: {
      supportsAlgae: false,
      productivity: 0,
      carryingCapacity: 1,
      nutrientEfficiency: 0.7,
      seasonalAmplitude: 0,
      consumerPressure: 1,
    },
  },
  {
    index: 10,
    orbitRadius: 0.12,
    bodyRadius: 0.005,
    phase: 1.36,
    angularSpeed: -0.84,
    mass: 160,
    orbitalMass: 0.001,
    parentIndex: 2,
    atmosphereHeight: 0,
    atmosphereDamping: 0,
    composition: world('Chalk Whisper', 190, climateGeometry(550000, 0.3, 1800, 0.22), { carbonate: 0.85, silica: 0.3, water: 0.008, nitrogen: 0.002 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 0, 0, 0, 0.22, [0.2, 0.12, 0.025], [0.76, 0.61, 0.16], [0.04, 0.04, 0.03], [0.7, 0.55, 0.16]),
    spinRate: -0.12,
    spinOffset: 1.1,
    seasonPhase: 9 * 1.7,
    ecology: {
      supportsAlgae: false,
      productivity: 0,
      carryingCapacity: 1,
      nutrientEfficiency: 0.7,
      seasonalAmplitude: 0,
      consumerPressure: 1,
    },
  },
  {
    index: 11,
    orbitRadius: 0.14,
    bodyRadius: 0.006,
    phase: 0.42,
    angularSpeed: 0.92,
    mass: 200,
    orbitalMass: 0.002,
    parentIndex: 3,
    atmosphereHeight: 0,
    atmosphereDamping: 0,
    composition: world('Quiet Pebble', 250, climateGeometry(700000, 0.4, 2000, 0.39), { silica: 0.65, basalt: 0.3, iron: 0.1, water: 0.003 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 0, 0.08, 0, 0.39, [0.18, 0.2, 0.21], [0.68, 0.73, 0.72], [0.08, 0.2, 0.28], [0.52, 0.66, 0.72]),
    spinRate: 0.17,
    spinOffset: -0.7,
    seasonPhase: 10 * 1.7,
    ecology: {
      supportsAlgae: false,
      productivity: 0,
      carryingCapacity: 1,
      nutrientEfficiency: 0.7,
      seasonalAmplitude: 0,
      consumerPressure: 1,
    },
  },
  {
    index: 12,
    orbitRadius: 0.11,
    bodyRadius: 0.0045,
    phase: -2.08,
    angularSpeed: 0.76,
    mass: 150,
    orbitalMass: 0.0008,
    parentIndex: 4,
    atmosphereHeight: 0,
    atmosphereDamping: 0,
    composition: world('Red Echo', 210, climateGeometry(400000, 0.15, 1500, 0.56), { ironOxide: 0.8, silica: 0.4, carbon: 0.1 }),
    appearance: bodyAppearance(BodySurfaceClass.Desert, 0, 0, 0, 0.56, [0.16, 0.025, 0.015], [0.55, 0.12, 0.035], [0.04, 0.03, 0.03], [0.52, 0.2, 0.12]),
    spinRate: 0.11,
    spinOffset: 2,
    seasonPhase: 11 * 1.7,
    ecology: {
      supportsAlgae: false,
      productivity: 0,
      carryingCapacity: 1,
      nutrientEfficiency: 0.7,
      seasonalAmplitude: 0,
      consumerPressure: 1,
    },
  },
  {
    index: 13,
    orbitRadius: 0.22,
    bodyRadius: 0.007,
    phase: 2.45,
    angularSpeed: 0.68,
    mass: 2000,
    orbitalMass: 0.003,
    parentIndex: 5,
    atmosphereHeight: 0.006,
    atmosphereDamping: 0.8,
    composition: world('Silt Cradle', 282, climateGeometry(1900000, 2.4, 320, 0.69), { nitrogen: 0.25, carbonDioxide: 0.09, oxygen: 0.002, water: 0.35, basalt: 0.6, ironOxide: 0.22, silicate: 0.18 },
    { internalHeatFluxWm2: 210 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 0.58, 0.35, 0.3, 0.69, [0.11, 0.075, 0.04], [0.42, 0.3, 0.14], [0.012, 0.2, 0.35], [0.18, 0.62, 0.92]),
    spinRate: 0.09,
    spinOffset: -1.4,
    seasonPhase: 12 * 1.7,
    ecology: {
      supportsAlgae: true,
      productivity: 0.78,
      carryingCapacity: 179.2,
      nutrientEfficiency: 0.88,
      seasonalAmplitude: 0.12,
      consumerPressure: 1,
    },
  },
  {
    index: 14,
    orbitRadius: 0.22,
    bodyRadius: 0.006,
    phase: -0.32,
    angularSpeed: -0.48,
    mass: 220,
    orbitalMass: 0.0015,
    parentIndex: 6,
    atmosphereHeight: 0,
    atmosphereDamping: 0,
    composition: world('Bitter Pearl', 215, climateGeometry(1100000, 0.9, 1500, 0.78), { nitrogen: 0.7, ammonia: 0.65, water: 0.1, silicate: 0.7, basalt: 0.3 },
    { internalHeatFluxWm2: 18, thermalCapacity: 15 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 0, 0, 0, 0.78, [0.13, 0.12, 0.1], [0.57, 0.5, 0.36], [0.03, 0.04, 0.05], [0.58, 0.52, 0.42]),
    spinRate: -0.1,
    spinOffset: 0.6,
    seasonPhase: 13 * 1.7,
    ecology: {
      supportsAlgae: false,
      productivity: 0,
      carryingCapacity: 1,
      nutrientEfficiency: 0.7,
      seasonalAmplitude: 0,
      consumerPressure: 1,
    },
  },
  {
    index: 15,
    orbitRadius: 0.21,
    bodyRadius: 0.006,
    phase: 2.82,
    angularSpeed: -0.56,
    mass: 180,
    orbitalMass: 0.0014,
    parentIndex: 7,
    atmosphereHeight: 0,
    atmosphereDamping: 0,
    composition: world('Pitch Seed', 100, climateGeometry(900000, 0.5, 2400, 0.88), { nitrogen: 1.3, methane: 0.8, water: 0.2, carbon: 0.3, basalt: 0.25 },
    { thermalCapacity: 15 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 0, 0.18, 0, 0.88, [0.2, 0.24, 0.25], [0.72, 0.8, 0.82], [0.035, 0.18, 0.28], [0.5, 0.78, 0.9]),
    spinRate: 0.13,
    spinOffset: 1.8,
    seasonPhase: 14 * 1.7,
    ecology: {
      supportsAlgae: false,
      productivity: 0,
      carryingCapacity: 1,
      nutrientEfficiency: 0.7,
      seasonalAmplitude: 0,
      consumerPressure: 1,
    },
  },
  {
    index: 16,
    orbitRadius: 0.21,
    bodyRadius: 0.006,
    phase: -1.18,
    angularSpeed: 0.52,
    mass: 190,
    orbitalMass: 0.0015,
    parentIndex: 8,
    atmosphereHeight: 0,
    atmosphereDamping: 0,
    composition: world('Cold Cinder', 110, climateGeometry(650000, 0.35, 1700, 0.96), { basalt: 0.72, iron: 0.3, carbon: 0.16, silica: 0.08 }),
    appearance: bodyAppearance(BodySurfaceClass.Rocky, 0, 0, 0, 0.96, [0.055, 0.06, 0.075], [0.35, 0.38, 0.44], [0.02, 0.03, 0.06], [0.3, 0.36, 0.5]),
    spinRate: 0.18,
    spinOffset: -0.2,
    seasonPhase: 15 * 1.7,
    ecology: {
      supportsAlgae: false,
      productivity: 0,
      carryingCapacity: 1,
      nutrientEfficiency: 0.7,
      seasonalAmplitude: 0,
      consumerPressure: 1,
    },
  },
];

function bodyAppearance(
  surfaceClass: BodySurfaceClass,
  atmosphereStrength: number,
  liquidLevel: number,
  greenLifeStrength: number,
  terrainSeed: number,
  lowColor: readonly [number, number, number],
  highColor: readonly [number, number, number],
  liquidColor: readonly [number, number, number],
  atmosphereColor: readonly [number, number, number],
): BodyAppearance {
  return {
    surfaceClass,
    atmosphereStrength,
    liquidLevel,
    greenLifeStrength,
    terrainSeed,
    lowColor,
    highColor,
    liquidColor,
    atmosphereColor,
  };
}
